/**
 * @fileOverview Where in the product a step is being read, taken from the frame itself: the record
 * whose page it is, and the tab that is open on it. See docs/view-help.md ("Where a step is").
 *
 * Read off the rendered page rather than declared in the catalog, for two reasons. It is always what
 * is on screen — the pointer clicks a tab mid-step, and the trail follows it — and it needs no line
 * per step in eleven tours, where a record renamed in the sample data would otherwise leave eleven
 * labels quietly wrong.
 *
 * Deliberately not the URL. `/employee/demo-1#company` is the developers' name for a page the product
 * calls "Max Mustermann · Unternehmens-relevant", and its sample id is one a reader would go looking
 * for in their own workspace and never find.
 */

/** The heading of a record's page — the entity card's title, on every detail and create view. */
const RECORD_TITLE = ".console-who__name"

/** Whatever tab strips the page has, each contributing the one that is open. */
const SELECTED_TAB = '[role="tab"][aria-selected="true"], .v-tab--selected'

/** A count beside a tab's name — the nav strip's queue sizes, a register's price-gap badge. */
const BADGE = ".v-badge__badge"

/** A label worth showing: something is written, and it is not a sentence. */
const usable = (text: string | null | undefined): text is string =>
  Boolean(text && text.length <= 40 && !text.includes("\n"))

/** Laid out, so a tab strip inside a closed dialog does not speak for the page behind it. */
const shown = (el: HTMLElement) => el.offsetParent !== null || el.getClientRects().length > 0

/**
 * A tab's name without its badge. The badge sits inside the tab, so its count comes out of
 * `textContent` glued to the name — "Mitarbeiter 2" for a strip carrying a queue size.
 */
function labelOf(tab: HTMLElement): string {
  const copy = tab.cloneNode(true) as HTMLElement
  copy.querySelectorAll(BADGE).forEach(badge => badge.remove())
  return copy.textContent?.trim() ?? ""
}

/**
 * The trail inside `frame`, coarsest first: the page's own heading, then the tab open on it — for the
 * employee page, `["Max Mustermann", "Unternehmens-relevant"]`. Empty on a plain list page, whose name
 * the walkthrough already knows from the catalog.
 */
export function readFrameTrail(frame: Window): string[] {
  const doc = frame.document
  const crumbs: string[] = []

  const title = doc.querySelector<HTMLElement>(RECORD_TITLE)
  if (title && shown(title) && usable(title.textContent?.trim())) crumbs.push(title.textContent!.trim())

  for (const tab of doc.querySelectorAll<HTMLElement>(SELECTED_TAB)) {
    // A tab that is a link is a page, not a part of one: the feature's own strip names the view the
    // header already names from the catalog, and in its own longer words ("Zeiten prüfen" for
    // Prüfung), which reads as the same thing said twice. The tabs worth having are the ones that
    // swap a panel inside the page.
    if (!shown(tab) || tab.closest("a")) continue
    const label = labelOf(tab)
    if (!usable(label)) continue
    crumbs.push(label)
    // The page's own strip, and no deeper: a register can hold a second strip of its own, and
    // "Projekt › Musterprojekt Nord › Stundenlohnarbeiten › Allgemein" says one thing too many.
    break
  }

  return crumbs.filter((crumb, index) => crumbs.findIndex(it => it.toLowerCase() === crumb.toLowerCase()) === index)
}
